import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import Button from "components/atoms/Button";
import { buildTpCodeMap } from "./utils";

const QRCodeModal = ({ isOpen, onClose, coordinate }) => {
  if (!isOpen) return null;
  const qrValue = coordinate
    ? `https://www.google.com/maps?q=${coordinate}`
    : "";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          textAlign: "center",
          minWidth: "300px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        }}
      >
        <h4 style={{ marginBottom: "20px" }}>QR Code Google Maps</h4>
        {qrValue ? (
          <div style={{ marginBottom: "20px" }}>
            <QRCodeSVG value={qrValue} size={200} />
          </div>
        ) : (
          <p style={{ marginBottom: "20px" }}>Koordinat tidak tersedia.</p>
        )}
        {coordinate && (
          <p style={{ marginBottom: "20px", color: "#666" }}>{coordinate}</p>
        )}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            type="button"
            onClick={onClose}
            variant="primary"
            size="normal"
          >
            Tutup
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function TableComponent({
  currentLevel,
  reportsData,
  tableRef,
  reportInfo,
}) {
  const [qrModal, setQrModal] = useState({ isOpen: false, coordinate: "" });

  const levelLower = String(currentLevel || "").toLowerCase();
  const isNational = levelLower === "national";
  const isProvince = levelLower === "province";

  let levelName = "";
  if (isNational) {
    levelName = "Nasional";
  } else if (isProvince) {
    levelName = "Provinsi";
  } else if (levelLower === "district") {
    levelName = "Kabupaten/Kota";
  } else {
    levelName = "Fasyankes";
  }

  const faskesName = (() => {
    const data = reportInfo?.fasyankes;
    if (!data) return "Semua Fasyankes";
    const parts = data.split(",");
    const names = parts.map((part) => part?.split("|")[1] || part);
    return names.join(", ");
  })();

  const regionColsCount = isNational ? 3 : isProvince ? 2 : 1;
  const allColumnsCount = 1 + regionColsCount + 13;

  const dataRows = reportsData ?? [];
  const tpCodeMap = useMemo(() => buildTpCodeMap(dataRows), [dataRows]);

  return (
    <div className="table-table-responsive" ref={tableRef}>
      <table>
        <tbody>
          <tr style={{ border: "none", padding: 0 }}>
            <th style={{ border: "none", paddingBottom: "0" }}>
              <h3>SISMAL v3, LAPORAN REKAP MASTER</h3>
            </th>
          </tr>
        </tbody>
      </table>
      <table>
        <tbody>
          <tr style={{ border: "none" }}>
            <th
              width="200"
              style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}
            >
              Periode Laporan
            </th>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              : {reportInfo?.statusPeriode || "-"}
            </th>
          </tr>
          <tr style={{ border: "none" }}>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              Tahun
            </th>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              : {reportInfo?.periode || "-"}
            </th>
          </tr>
          <tr style={{ border: "none" }}>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              Bulan
            </th>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              : {reportInfo?.bulan || "Semua Bulan"}
            </th>
          </tr>
          <tr style={{ border: "none" }}>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              Level
            </th>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              : {levelName}
            </th>
          </tr>
          <tr style={{ border: "none" }}>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              Provinsi
            </th>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              :{" "}
              {!reportInfo?.province
                ? "Semua Provinsi"
                : reportInfo?.province?.split("|")[1] || reportInfo?.province}
            </th>
          </tr>
          <tr style={{ border: "none" }}>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              Kab/Kota
            </th>
            <th style={{ border: "none", paddingTop: 0, paddingBottom: 0 }}>
              :{" "}
              {!reportInfo?.district
                ? "Semua Kab/Kota"
                : reportInfo?.district?.split("|")[1] || reportInfo?.district}
            </th>
          </tr>
          <tr style={{ border: "none" }}>
            <th style={{ border: "none", paddingTop: 0 }}>Fasyankes</th>
            <th style={{ border: "none", paddingTop: 0 }}>: {faskesName}</th>
          </tr>
        </tbody>
      </table>

      <table>
        <thead>
          <tr>
            <th>No.</th>
            {isNational && <th>Provinsi</th>}
            {(isNational || isProvince) && <th>Kabupaten/Kota</th>}
            <th>Fasyankes</th>
            <th>Kode TP</th>
            <th>Barcode</th>
            <th>Desa</th>
            <th>Dusun</th>
            <th>Nama TP</th>
            <th>Jenis TP Awal</th>
            <th>Petugas/Tim Awal</th>
            <th>Tanggal Survei Pertama</th>
            <th>Tanggal Survei Terakhir</th>
            <th>Status TP Terakhir</th>
            <th>Penyebab Hilangnya TP</th>
            <th>Jumlah Kunjungan/ Survei</th>
            <th>Kepadatan Jentik Terakhir</th>
          </tr>
        </thead>
        <tbody>
          {dataRows.length ? (
            dataRows.map((row, idx) => (
              <tr key={row?.tp_id ?? idx}>
                <td>{idx + 1}</td>
                {isNational && <td>{row?.provinsi ?? row?.province ?? "-"}</td>}
                {(isNational || isProvince) && (
                  <td>{row?.kabupaten ?? row?.district ?? "-"}</td>
                )}
                <td>{row?.fasyankes ?? "-"}</td>
                <td>{tpCodeMap.get(row?.tp_id) ?? "-"}</td>
                <td>
                  {row?.koordinat_terakhir ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="small"
                      onClick={() =>
                        setQrModal({
                          isOpen: true,
                          coordinate: row.koordinat_terakhir,
                        })
                      }
                    >
                      Lihat QR
                    </Button>
                  ) : (
                    "-"
                  )}
                </td>
                <td>{row?.desa || "-"}</td>
                <td>{row?.dusun || "-"}</td>
                <td>{row?.nama_tp || "-"}</td>
                <td>{row?.jenis_tp_awal || "-"}</td>
                <td>{row?.petugas_awal || "-"}</td>
                <td>{row?.tanggal_survei_pertama || "-"}</td>
                <td>{row?.tanggal_survei_terakhir || "-"}</td>
                <td>{row?.status_terakhir || "-"}</td>
                <td>{row?.penyebab_hilang_tp || "-"}</td>
                <td>{row?.jumlah_kunjungan ?? "-"}</td>
                <td>
                  {row?.kepadatan_jentik_terakhir != null
                    ? Number(row.kepadatan_jentik_terakhir).toFixed(2)
                    : "-"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={allColumnsCount} style={{ textAlign: "center" }}>
                Belum ada data
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <QRCodeModal
        isOpen={qrModal.isOpen}
        onClose={() => setQrModal({ isOpen: false, coordinate: "" })}
        coordinate={qrModal.coordinate}
      />
    </div>
  );
}
